import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { createSalesEndpoints } from '../api/salesApi';

const identityRows = (rows) => rows;

export const getSalesValue = (item, ...keys) => {
  if (!item) return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(item, key) && item[key] !== undefined) {
      return item[key];
    }
  }

  return undefined;
};

export const getSalesId = (item) => getSalesValue(item, 'id', 'Id', 'ID');

export const isDeletedSalesRow = (item) => {
  const deleted = getSalesValue(item, 'deleted', 'Deleted');
  if (deleted === true || deleted === 1) return true;
  if (typeof deleted !== 'string') return false;

  const normalized = deleted.trim().toLowerCase();
  return normalized === 'true' || normalized === '1';
};

export const getSalesArrayPayload = (response) => {
  let rows = [];
  if (Array.isArray(response)) rows = response;
  else if (Array.isArray(response?.data)) rows = response.data;
  else if (Array.isArray(response?.Data)) rows = response.Data;
  else if (Array.isArray(response?.items)) rows = response.items;
  else if (Array.isArray(response?.Items)) rows = response.Items;
  else if (Array.isArray(response?.result)) rows = response.result;
  else if (Array.isArray(response?.Result)) rows = response.Result;

  return rows.filter((row) => !isDeletedSalesRow(row));
};

export const formatSalesApiError = (error, fallback = 'Unable to load Sales data.') =>
  error?.message || error?.data?.message || fallback;

export const useSalesEndpoints = () => {
  const apiUrls = useSelector(selectApiUrls);
  return useMemo(() => createSalesEndpoints(apiUrls), [apiUrls]);
};

export const useSalesApiList = (getUrl, options = {}) => {
  const endpoints = useSalesEndpoints();
  const request = usePortalApiRequest();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    enabled = true,
    normalize = identityRows,
    errorMessage = 'Unable to load Sales data.',
  } = options;

  const resolvedUrl = enabled
    ? (typeof getUrl === 'function' ? getUrl(endpoints) : getUrl)
    : '';

  const load = useCallback(async () => {
    if (!resolvedUrl) {
      setItems([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError('');

    try {
      const response = await request({ method: 'GET', url: resolvedUrl });
      const rows = normalize(getSalesArrayPayload(response));
      setItems(rows);
      return rows;
    } catch (requestError) {
      setError(formatSalesApiError(requestError, errorMessage));
      setItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [errorMessage, normalize, request, resolvedUrl]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, setItems, loading, error, reload: load, request, endpoints, resolvedUrl };
};
